import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Video, Play, Calendar, User } from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => { fetchVideos(); }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmbedUrl = (video) => {
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (match) return `https://www.youtube.com/embed/${match[1]}`;
    } else if (video.video_type === 'vimeo') {
      const match = video.video_url.match(/vimeo\.com\/(\d+)/);
      if (match) return `https://player.vimeo.com/video/${match[1]}`;
    }
    return null;
  };

  const getThumbnail = (video) => {
    if (video.thumbnail_url) return video.thumbnail_url;
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (match) return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-hermes-400 pt-20">
      <div className="fixed top-24 left-8 z-40">
        <img src="/logo-quid-novi.png" alt="Quid Novi" className="h-20 w-20" />
      </div>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">Interviews</h1>
          <p className="text-xl text-white/90 max-w-3xl">Découvrez nos auteurs à travers leurs interviews vidéo.</p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12"><p className="text-white/80">Chargement...</p></div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12"><p className="text-white/80">Aucune interview pour le moment.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <article key={video.id} className="bg-white shadow-lg overflow-hidden group cursor-pointer" onClick={() => setSelectedVideo(video)}>
                  <div className="aspect-video bg-stone-200 relative overflow-hidden">
                    {getThumbnail(video) ? (
                      <img src={getThumbnail(video)} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Video className="w-12 h-12 text-stone-400" /></div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 bg-hermes-600 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                    {video.duration && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">{video.duration}</span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-stone-900 line-clamp-2 mb-2">{video.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-stone-500">
                      <span className="flex items-center gap-1"><User className="w-4 h-4" />{video.author_name}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(video.publication_date).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedVideo(null)}>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe src={getEmbedUrl(selectedVideo)} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={selectedVideo.title} />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-white">{selectedVideo.title}</h3>
                <p className="text-white/70">{selectedVideo.author_name}</p>
              </div>
              <ShareButtons url={`https://quidnovi-editions.fr/videos/${selectedVideo.id}`} title={selectedVideo.title} description={selectedVideo.description} />
            </div>
            <button onClick={() => setSelectedVideo(null)} className="absolute top-4 right-4 text-white/70 hover:text-white text-4xl">&times;</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;
